import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import './embed.css';

const EmbedLeaderboardLogo = () => {
  const [searchParams] = useSearchParams();

  const bg = searchParams.get('bg') || '#0a0a0a';
  const limit = parseInt(searchParams.get('limit')) || 50;
  const refreshInterval = parseInt(searchParams.get('refresh')) || 5000;
  const accentColor = searchParams.get('accent') || '#ffd700';

  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);
  const prevRanksRef = useRef({});

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/leaderboard?limit=${limit}`);
      if (response.ok) {
        const data = await response.json();
        updateLeaderboard(data.slice(0, limit));
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeaderboard = (newData) => {
    const currentRanks = {};
    newData.forEach((user, index) => {
      const prevRank = prevRanksRef.current[user.id];
      currentRanks[user.id] = index + 1;
      user.rankChange = prevRank ? prevRank - (index + 1) : 0;
    });
    prevRanksRef.current = currentRanks;
    setLeaderboard(newData);
  };

  useEffect(() => {
    fetchLeaderboard();

    socketRef.current = io(API_BASE_URL, { auth: { public: true } });
    socketRef.current.on('leaderboard-update', (data) => {
      const list = Array.isArray(data) ? data : data?.leaderboard;
      if (Array.isArray(list)) {
        updateLeaderboard(list.slice(0, limit));
      }
    });

    const interval = setInterval(fetchLeaderboard, refreshInterval);
    return () => {
      if (socketRef.current) socketRef.current.disconnect();
      clearInterval(interval);
    };
  }, [limit, refreshInterval]);

  const getRankBadge = (rank) => {
    switch (rank) {
      case 1: return '\u{1F947}';
      case 2: return '\u{1F948}';
      case 3: return '\u{1F949}';
      default: return `#${rank}`;
    }
  };

  if (loading) {
    return (
      <div className="embed-lb-logo" style={{ background: bg }}>
        <div className="embed-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="embed-lb-logo" style={{ background: bg === 'transparent' ? 'transparent' : bg }}>
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>

      {/* Leaderboard — left 50% */}
      <div className="elbl-leaderboard">
        <div className="leaderboard-header" style={{ borderColor: accentColor }}>
          <span className="leaderboard-title">LEADERBOARD</span>
          <span className="leaderboard-count">{leaderboard.length} traders</span>
        </div>
        <div className="leaderboard-list">
          {leaderboard.length === 0 && (
            <div className="embed-waiting">Waiting for traders...</div>
          )}
          {leaderboard.map((user, index) => {
            const rank = index + 1;
            const pnl = user.totalValue - 10000;
            const pnlPercent = user.percentGain || 0;
            const isPositive = pnl >= 0;
            const isTop3 = rank <= 3;

            return (
              <div
                key={user.id}
                className={`leaderboard-item ${isTop3 ? 'top-three' : ''} ${user.rankChange !== 0 ? 'rank-changed' : ''}`}
                style={{ '--rank-change': user.rankChange, '--accent': accentColor }}
              >
                <div className={`item-rank ${isTop3 ? `rank-${rank}` : ''}`}>
                  {isTop3 ? getRankBadge(rank) : <span className="rank-number">{rank}</span>}
                </div>
                <div className="item-info">
                  <span className="item-name">
                    {user.username || user.displayName || user.shortId || `Trader ${user.id?.slice(-4)}`}
                  </span>
                </div>
                <div className="item-value">
                  <span className="item-portfolio">{formatCurrency(user.totalValue)}</span>
                  <span className={`item-pnl ${isPositive ? 'positive' : 'negative'}`}>
                    {isPositive ? '+' : ''}{formatCurrency(pnl)}
                    <span className="pnl-percent">({isPositive ? '+' : ''}{pnlPercent.toFixed(1)}%)</span>
                  </span>
                </div>

                {user.rankChange !== 0 && (
                  <div className={`rank-change-indicator ${user.rankChange > 0 ? 'up' : 'down'}`}>
                    {user.rankChange > 0 ? `\u2191${user.rankChange}` : `\u2193${Math.abs(user.rankChange)}`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Logo — right 50% */}
      <div className="elbl-logo-side">
        <img src="/komok_logo.webp" alt="KOMOK" className="elbl-logo-img" />
      </div>
    </div>
  );
};

export default EmbedLeaderboardLogo;
